import { onMounted, onUnmounted } from 'vue'

import { useWorkspace } from '@/features/account'

export interface AudienceEventBus {
  on: (name: 'custom_analysis.completed', handler: (payload: unknown) => void) => () => void
}

interface VisibilityDocument {
  readonly visibilityState: DocumentVisibilityState
  addEventListener: Document['addEventListener']
  removeEventListener: Document['removeEventListener']
}

export interface FunnelCompletionRuntimeDeps {
  bus: AudienceEventBus
  workspaceId: () => number | string | undefined
  reload: () => Promise<void>
  document?: VisibilityDocument
  debounceMs?: number
}

export interface FunnelCompletionRuntime {
  start: () => void
  stop: () => void
  whenIdle: () => Promise<void>
}

const FUNNEL_PARTITIONS = new Set(['custom_funnel_report_data', 'mmp_custom_funnel_analysis'])
let installedBus: AudienceEventBus | null = null

export function registerAudienceEventBus(bus: AudienceEventBus): void {
  installedBus = bus
}

export function resetAudienceEventBusForTest(): void {
  installedBus = null
}

function requiredBus(): AudienceEventBus {
  if (!installedBus) throw new Error('Audience event bus must be registered before page mount')
  return installedBus
}

function eventFields(payload: unknown): { workspaceId: string; partitionName: string } | null {
  if (!payload || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  if (row.workspace_id === undefined || typeof row.partition_name !== 'string') return null
  return { workspaceId: String(row.workspace_id), partitionName: row.partition_name }
}

export function createFunnelCompletionRuntime(
  deps: FunnelCompletionRuntimeDeps,
): FunnelCompletionRuntime {
  const visibilityDocument = deps.document ?? document
  const debounceMs = deps.debounceMs ?? 750
  const pending = new Set<Promise<void>>()
  let unsubscribe: (() => void) | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let wasHidden = false

  function track(job: Promise<void>): void {
    pending.add(job)
    void job.finally(() => pending.delete(job))
  }

  function scheduleReload(): void {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      track(deps.reload())
    }, debounceMs)
  }

  function onCompleted(payload: unknown): void {
    const event = eventFields(payload)
    const workspaceId = deps.workspaceId()
    if (
      !event ||
      workspaceId === undefined ||
      event.workspaceId !== String(workspaceId) ||
      !FUNNEL_PARTITIONS.has(event.partitionName)
    ) {
      return
    }
    scheduleReload()
  }

  function onVisibilityChange(): void {
    if (visibilityDocument.visibilityState === 'hidden') {
      wasHidden = true
      return
    }
    if (!wasHidden) return
    wasHidden = false
    track(deps.reload())
  }

  return {
    start: () => {
      if (unsubscribe) return
      unsubscribe = deps.bus.on('custom_analysis.completed', onCompleted)
      visibilityDocument.addEventListener('visibilitychange', onVisibilityChange)
    },
    stop: () => {
      unsubscribe?.()
      unsubscribe = null
      visibilityDocument.removeEventListener('visibilitychange', onVisibilityChange)
      if (timer) clearTimeout(timer)
      timer = null
    },
    whenIdle: async () => {
      await Promise.all([...pending])
    },
  }
}

export function useFunnelCompletionStream(
  reload: () => Promise<void>,
  workspaceId?: () => number | string | undefined,
): void {
  const workspace = useWorkspace()
  let runtime: FunnelCompletionRuntime | null = null
  onMounted(() => {
    runtime = createFunnelCompletionRuntime({
      bus: requiredBus(),
      workspaceId: workspaceId ?? (() => workspace.workspaceId.value),
      reload,
    })
    runtime.start()
  })
  onUnmounted(() => runtime?.stop())
}
